import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Star } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/testimonials")({
  head: () => ({ meta: [{ title: "Testimonials Manager" }, { name: "robots", content: "noindex" }] }),
  component: TestimonialsPage,
});

type T = {
  id?: string;
  author_name: string;
  author_location: string | null;
  quote: string;
  rating: number;
  featured: boolean;
  published: boolean;
  sort_order: number;
};

const empty = (): T => ({
  author_name: "", author_location: "", quote: "", rating: 5,
  featured: false, published: false, sort_order: 0,
});

function TestimonialsPage() {
  const [list, setList] = useState<T[]>([]);
  const [current, setCurrent] = useState<T | null>(null);

  const load = async () => {
    const { data } = await supabase.from("testimonials").select("*").order("sort_order", { ascending: true }).order("created_at", { ascending: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current || !current.author_name || !current.quote) return toast.error("Author and quote required");
    if (current.id) {
      const { error } = await supabase.from("testimonials").update(current).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("testimonials").insert(current as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("testimonials").delete().eq("id", id); load(); } };
  const togglePublished = async (t: T) => {
    await supabase.from("testimonials").update({ published: !t.published }).eq("id", t.id!);
    load();
  };

  if (!current) return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Testimonials</h1>
          <p className="text-sm text-muted-foreground mt-1">Curate customer quotes that appear on the public site.</p>
        </div>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New</Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {list.map(t => (
          <div key={t.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{t.author_name}</h3>
                <p className="text-xs text-muted-foreground">{t.author_location}</p>
                <div className="flex mt-1">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}</div>
              </div>
              <button onClick={() => del(t.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="text-sm mt-3 italic line-clamp-3">"{t.quote}"</p>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 ${t.published ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted'}`}>{t.published ? 'Published' : 'Draft'}</span>
              {t.featured && <span className="rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-600">Featured</span>}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => setCurrent(t)}>Edit</Button>
              <Button size="sm" variant="secondary" onClick={() => togglePublished(t)}>{t.published ? 'Unpublish' : 'Publish'}</Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-muted-foreground text-sm">No testimonials yet.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div><label className="text-xs text-muted-foreground">Author Name</label><Input value={current.author_name} onChange={e => setCurrent({ ...current, author_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Location</label><Input placeholder="e.g. Highlands Ranch, CO" value={current.author_location || ""} onChange={e => setCurrent({ ...current, author_location: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Rating (1-5)</label><NumberInput min={1} max={5} value={current.rating} onValueChange={n => setCurrent({ ...current, rating: n })} /></div>
        <div><label className="text-xs text-muted-foreground">Sort Order</label><NumberInput value={current.sort_order} onValueChange={n => setCurrent({ ...current, sort_order: n })} /></div>
        <div className="flex items-end"><label className="text-sm flex items-center gap-2"><input type="checkbox" checked={current.featured} onChange={e => setCurrent({ ...current, featured: e.target.checked })} /> Featured</label></div>
        <div className="flex items-end"><label className="text-sm flex items-center gap-2"><input type="checkbox" checked={current.published} onChange={e => setCurrent({ ...current, published: e.target.checked })} /> Published (visible on site)</label></div>
      </div>
      <Textarea placeholder="Customer quote…" className="mt-4" rows={6} value={current.quote} onChange={e => setCurrent({ ...current, quote: e.target.value })} />
    </div>
  );
}
